import { useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import type { EnrichedParticipant } from "../../types/participants";

interface Props {
  tripId: string;
  participants: EnrichedParticipant[];
  onSaved: () => void;
  onCancel?: () => void;
  isOrganizer: boolean;
  initialUserId?: string;
  initialAmount?: string;
}

export default function PaymentForm({
  tripId,
  participants,
  onSaved,
  onCancel,
  isOrganizer,
  initialUserId,
  initialAmount,
}: Props) {
  const [selectedUser, setSelectedUser] = useState(initialUserId ?? "");
  const [amount, setAmount] = useState(initialAmount ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const parsed = parseFloat(amount);
    if (!selectedUser || isNaN(parsed) || parsed <= 0) return;

    setSaving(true);

    const { error } = await supabase.from("trip_payments").insert({
      trip_id: tripId,
      user_id: selectedUser === "__shared__" ? null : selectedUser,
      amount: parsed,
    });

    if (error) {
      console.error("❌ Fehler beim Speichern der Zahlung:", error.message);
    } else {
      setAmount("");
      setSelectedUser("");
      onSaved(); // trigger refresh
    }
    setSaving(false);
  };

  if (!isOrganizer) return null;

  return (
    <>
      <h3 className="text-[10px] uppercase tracking-widest text-slate-600 mb-3">
        Zahlung erfassen
      </h3>

      <select
        className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
        value={selectedUser}
        onChange={(e) => setSelectedUser(e.target.value)}
      >
        <option value="">– Teilnehmer auswählen –</option>
        <option value="__shared__">💰 Allgemein (für alle)</option>
        {participants
          .filter((p) => !p.is_blocked)
          .map((p) => {
            const name =
              `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email;
            return (
              <option key={p.user_id} value={p.user_id}>
                {name}
              </option>
            );
          })}
      </select>

      <input
        type="number"
        step="0.01"
        min="0"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder="Betrag (z. B. 10.00)"
        className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-5 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
      />

      <div className="flex justify-end gap-2">
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-xs px-3 py-1.5 rounded-full bg-[#1e2639] text-slate-400 hover:text-slate-200 transition-colors"
          >
            Abbrechen
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors"
        >
          {saving ? "Speichern…" : "Zahlung speichern"}
        </button>
      </div>
    </>
  );
}
